var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-buildify.ts
import { A } from "@adornis/base/env-info.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { ContextProvider, createContext } from "@lit/context";
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { combineLatest, debounceTime, distinctUntilChanged, fromEvent, startWith, takeUntil } from "rxjs";
import { BuildifyGlobalSettings } from "../db/GlobalSettings.js";
import "./components/x-buildify-global-settings.js";
import "./editors/x-buildify-inline-editor.js";
import "./extensions/ContentExtensionBoilerplate.js";
import { DocumentExtension } from "./extensions/DocumentExtension.js";
import { Contexts, Event, Size } from "./globals/enums.js";
import { copyClipboardContent } from "./globals/methods.js";
import "./x-buildify-dropzone.js";
import "./x-buildify-toolbar.js";
import "./x-buildify-visualization.js";
var XBuildify = class extends FormField {
  constructor() {
    super(...arguments);
    this.documentExtension = new RXController(this, DocumentExtension);
    this.customContent = new RXController(
      this,
      void 0
    );
    this.extensions = new RXController(this, []);
    this.options = new RXController(this, []);
    this.autoScrollContainer = new RXController(this, null);
    this.sizeBreakpoints = new RXController(this, {
      [Size.DESKTOP]: 1200,
      [Size.TABLET]: 992,
      [Size.MOBILE]: 768
    });
    this.isEditor = new RXController(this, false);
    this.globalSettingsClassName = new RXController(this, BuildifyGlobalSettings._class);
    this.hideCustomComponentID = new RXController(this, void 0);
    this.formController = new RXController(this, null);
    this._mode = new RXController(this, "edit");
    this._identifierProvider = new ContextProvider(this, {
      context: createContext(Contexts.IDENTIFIER),
      initialValue: A.getGloballyUniqueID()
    });
    this._globalSettingsClassNameProvider = new ContextProvider(this, {
      context: createContext(Contexts.GLOBAL_SETTINGS_CLASS_NAME)
    });
    this._extensionProvider = new ContextProvider(this, {
      context: createContext(Contexts.EXTENSIONS)
    });
    this._dataProvider = new ContextProvider(this, {
      context: createContext(Contexts.CONTENT)
    });
    this._anchorIDProvider = new ContextProvider(this, {
      context: createContext(Contexts.ANCHOR_ID)
    });
    this._formProvider = new ContextProvider(this, {
      context: createContext(Contexts.FORM)
    });
    this._lastEditContentHost = void 0;
    this._editContentProvider = new ContextProvider(this, {
      context: createContext(Contexts.EDIT_CONTENT)
    });
    this._draggingProvider = new ContextProvider(this, {
      context: createContext(Contexts.DRAGGING),
      initialValue: false
    });
    this._clipboardProvider = new ContextProvider(this, {
      context: createContext(Contexts.CLIPBOARD)
    });
    this._sizeProvider = new ContextProvider(this, {
      context: createContext(Contexts.SIZE),
      initialValue: null
    });
    this._modeProvider = new ContextProvider(this, {
      context: createContext(Contexts.MODE)
    });
    this._optionsProvider = new ContextProvider(this, {
      context: createContext(Contexts.OPTIONS)
    });
    this._customContentProvider = new ContextProvider(this, {
      context: createContext(Contexts.CUSTOM_CONTENT)
    });
    this._sizeBreakpointProvider = new ContextProvider(this, {
      context: createContext(Contexts.SIZE_BREAKPOINT)
    });
    this._hideCustomComponentIDProvider = new ContextProvider(this, {
      context: createContext(Contexts.HIDE_CUSTOM_COMPONENT_ID)
    });
    this._autoScrollContainerProvider = new ContextProvider(this, { context: createContext(Contexts.AUTO_SCROLL_CONTAINER) });
    this._globalSettingsDrawer = createRef();
  }
  connectedCallback() {
    super.connectedCallback();
    this.isEditor.observable.subscribe((isEditor) => {
      if (!isEditor && this._mode.value === "edit")
        this._mode.next("view");
    });
    combineLatest([
      this.sizeBreakpoints.observable.pipe(startWith(null), distinctUntilChanged()),
      this.extensions.observable.pipe(startWith(null), distinctUntilChanged()),
      this.value.observable.pipe(startWith(null), distinctUntilChanged()),
      this.hideCustomComponentID.observable.pipe(startWith(null), distinctUntilChanged()),
      this.formController.observable.pipe(startWith(null), distinctUntilChanged()),
      this._mode.observable.pipe(startWith(null), distinctUntilChanged()),
      this.customContent.observable.pipe(startWith(null), distinctUntilChanged()),
      this.globalSettingsClassName.observable.pipe(startWith(null), distinctUntilChanged())
    ]).pipe(debounceTime(300), takeUntil(this.disconnected)).subscribe(
      ([
        breakPoints,
        extensions,
        content,
        hideCustomComponent,
        formController,
        mode,
        customContent,
        globalSettingsClassName
      ]) => {
        if (extensions)
          this._extensionProvider.setValue(extensions);
        this._dataProvider.setValue(content);
        this._hideCustomComponentIDProvider.setValue(hideCustomComponent, true);
        this._formProvider.setValue(formController, true);
        this._modeProvider.setValue(mode, true);
        this._sizeBreakpointProvider.setValue(breakPoints, true);
        if (globalSettingsClassName)
          this._globalSettingsClassNameProvider.setValue(globalSettingsClassName, true);
        if (customContent)
          this._customContentProvider.setValue(customContent, true);
      }
    );
    this.options.observable.pipe(takeUntil(this.disconnected)).subscribe((options) => this._optionsProvider.setValue(options, true));
    fromEvent(this, Event.SET_EDIT_CONTENT).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      if (!(e instanceof CustomEvent))
        return;
      const value = e.detail.value;
      this._editContentProvider.setValue(value, true);
      const host = e.detail.host;
      if (host === this._lastEditContentHost)
        this._lastEditContentHost = null;
      host._editContentInjected = value;
      if (this._lastEditContentHost)
        this._lastEditContentHost._editContentInjected = null;
      this._lastEditContentHost = host;
    });
    fromEvent(this, Event.SET_CONTENT).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      this._dataProvider.setValue(e.detail.value, true);
      this.requestUpdate();
    });
    fromEvent(this, Event.UPDATE_CONTENT).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      this._dataProvider.updateObservers();
    });
    fromEvent(this, Event.MOVE_TO_ANCHOR).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      this._anchorIDProvider.setValue(e.detail.value, true);
    });
    fromEvent(this, Event.DRAGGING_CHANGED).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      if (this._draggingProvider.value === e.detail.value) {
        return;
      }
      if (!(e instanceof CustomEvent))
        return;
      const value = e.detail.value;
      this._draggingProvider.setValue(value, true);
    });
    fromEvent(this, Event.COPY_TO_CLIPBOARD).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      if (!(e instanceof CustomEvent))
        return;
      const value = e.detail.value;
      if (!value)
        return this._clipboardProvider.setValue(null, true);
      const copiedContent = copyClipboardContent({
        contentID: value._id,
        extensions: this._extensionProvider.value,
        content: this._dataProvider.value
      });
      this._clipboardProvider.setValue(copiedContent, true);
    });
    fromEvent(this, Event.SIZE_CHANGED).pipe(takeUntil(this.disconnected)).subscribe((e) => {
      if (!(e instanceof CustomEvent))
        return;
      const value = e.detail.value;
      if (!value && !isNaN(value))
        return this._sizeProvider.setValue(null);
      if (Number.isNaN(value))
        throw new Error(`invalid value for size changed event: '${value}'`);
      if (!this.sizeBreakpoints.value)
        return null;
      if (value <= this.sizeBreakpoints.value[Size.MOBILE])
        return this._sizeProvider.setValue(Size.MOBILE);
      if (value <= this.sizeBreakpoints.value[Size.TABLET])
        return this._sizeProvider.setValue(Size.TABLET);
      return this._sizeProvider.setValue(Size.DESKTOP);
    });
    fromEvent(this, Event.OPEN_SETTINGS).pipe(takeUntil(this.disconnected)).subscribe(async () => {
      const drawer = this._globalSettingsDrawer.value;
      if (!drawer)
        return;
      drawer.open();
      drawer.innerHTML = `
        <x-buildify-global-settings
          globalSettingsClassName=${this.globalSettingsClassName.value}
          @close=${() => this._globalSettingsDrawer.value?.close()}
        ></x-buildify-global-settings>
        `;
    });
    this.autoScrollContainer.observable.pipe(takeUntil(this.disconnected)).subscribe((autoScrollContainer) => this._autoScrollContainerProvider.setValue(autoScrollContainer));
  }
  render() {
    if (this._mode.value === "view" && !this.isEditor.value)
      return xComponents(this.__visualization());
    return xComponents(html`
      <x-buildify-inline-editor
        @switch-mode=${() => {
      if (this._mode.value === "edit")
        this._mode.next("view");
      else
        this._mode.next("edit");
    }}
      >
        ${this.__visualization()}
      </x-buildify-inline-editor>
      ${this._globalSettings()}
      <!--
      The slot is used to allow components that require any of the context providers present in x-buildify 
      to be a child of x-buildify, enabling them to access and utilize these providers,
      e.g. page settings that require some data from the global settings will need to access the global settings class name provider.
      -->
      <slot></slot>
    `);
  }
  __visualization() {
    return html`
      <x-buildify-visualization .documentExtension=${this.documentExtension.value}></x-buildify-visualization>
    `;
  }
  _globalSettings() {
    return html`
      <x-drawer-panel ${ref(this._globalSettingsDrawer)}>
        ${this._globalSettingsDrawer.value?.isOpen ? html` open ` : html` closed `}
        <!-- <x-buildify-global-settings
          .globalSettingsClassName=${this.globalSettingsClassName.value}
          @close=${() => this._globalSettingsDrawer.value?.close()}
        ></x-buildify-global-settings> -->
      </x-drawer-panel>
    `;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          display: "block"
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "documentExtension", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "customContent", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "extensions", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "options", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "autoScrollContainer", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "sizeBreakpoints", 2);
__decorateClass([
  property({ attribute: "editor", type: Boolean })
], XBuildify.prototype, "isEditor", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "globalSettingsClassName", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "hideCustomComponentID", 2);
__decorateClass([
  property({ attribute: false })
], XBuildify.prototype, "formController", 2);
__decorateClass([
  state()
], XBuildify.prototype, "_mode", 2);
XBuildify = __decorateClass([
  customElement("x-buildify")
], XBuildify);
export {
  XBuildify
};
